import { useCallback, useEffect, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { showMessage } from 'react-native-flash-message';
import { theme, Text, Icon, FormInput, Button } from '@socrait/react';

import { HyperLink } from '~/components/HyperLink';
import { Loading } from '~/components/Loading';
import { useUserProfile } from '~/services/useUserProfileQuery';
import { useResendVerificationEmailMutation } from '~/services/useResendVerificationEmailMutation';
import { useVerifyEmailMutation } from '~/services/useVerifyEmailMutation';
import { showError } from '~/utils/showError';
import { PublicPageLayout } from '~/layouts/PublicPageLayout';
import { ClientError } from '~/errors';

const RESEND_COOLDOWN_SECONDS = 30;

type TokenVerifyStatus = 'pending' | 'expired' | 'invalid' | 'failed';

export function VerifyPage() {
  const [resendCooldown, setResendCooldown] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [errorEmail, setErrorEmail] = useState<string | undefined>(undefined);
  const [inputEmail, setInputEmail] = useState('');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isMobileLink = searchParams.has('mobile');

  const [tokenVerifyStatus, setTokenVerifyStatus] = useState<TokenVerifyStatus | null>(token ? 'pending' : null);
  const [isMobileVerified, setIsMobileVerified] = useState(false);

  const { user } = useUserProfile();
  const { mutate: resend, status: resendStatus } = useResendVerificationEmailMutation();
  const { mutate: verifyEmail } = useVerifyEmailMutation();
  const navigate = useNavigate();
  const location = useLocation();
  // Email can come from router state (post-signup, no auth token yet) or from the user profile.
  const emailFromState = (location.state as { email?: string } | null)?.email;
  const displayEmail = emailFromState ?? user?.email;

  // In token-error cases, the backend echoes the email embedded in the token.
  // Fall back to user profile / signup state email, then to the manual input field.
  const resolvedEmail = errorEmail ?? displayEmail;

  const isTokenError =
    tokenVerifyStatus === 'expired' || tokenVerifyStatus === 'invalid' || tokenVerifyStatus === 'failed';
  const showEmailInput = isTokenError && !resolvedEmail;

  const isResendPending = resendStatus === 'pending';

  const startCooldown = useCallback((seconds: number) => {
    setResendCooldown(seconds);
    intervalRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    verifyEmail(
      { token },
      {
        onSuccess: () => {
          if (isMobileLink) {
            setIsMobileVerified(true);
          } else {
            showMessage({
              message: 'Your email has been successfully verified. Log in to continue.',
              type: 'success',
            });
            navigate('/login');
          }
        },
        onError: (error) => {
          if (error instanceof ClientError && error.errorCode === 'verification_token_expired') {
            setTokenVerifyStatus('expired');
            setErrorEmail(error.email);
          } else if (error instanceof ClientError && error.errorCode === 'verification_token_invalid') {
            setTokenVerifyStatus('invalid');
            setErrorEmail(error.email);
          } else {
            showMessage({ message: 'Verification failed. Please try resending.', type: 'warning' });
            setTokenVerifyStatus('failed');
          }
        },
      },
    );
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const resendEmail = resolvedEmail ?? (showEmailInput ? inputEmail : undefined);

  const handleResend = useCallback(() => {
    if (!resendEmail || isResendPending || resendCooldown > 0) return;
    resend(
      { email: resendEmail },
      {
        onSuccess: () => startCooldown(RESEND_COOLDOWN_SECONDS),
        onError: (error) => {
          if (error instanceof ClientError && error.statusCode === 429) {
            const seconds = error.retryAfter ?? RESEND_COOLDOWN_SECONDS;
            startCooldown(seconds);
          } else {
            showError(error, 'Failed to resend verification email. Please try again.');
          }
        },
      },
    );
  }, [resendEmail, isResendPending, resendCooldown, resend, startCooldown]);

  if (tokenVerifyStatus === 'pending') {
    return <Loading />;
  }

  if (isMobileVerified) {
    return (
      <PublicPageLayout>
        <View style={styles.container}>
          <Text style={styles.heading}>Email verified. You're now ready to use Socrait on your phone!</Text>
          <View style={styles.iconContainer}>
            <Icon type="email" width={200} height={200} fill={theme.colors.primaryPurple} />
          </View>
          <Text style={styles.prompt}>
            {'Want to explore your dashboard instead? '}
            <HyperLink
              onPress={() => navigate('/login', { replace: true })}
              variant="primary"
              style={styles.promptLink}>
              Log in here.
            </HyperLink>
          </Text>
        </View>
      </PublicPageLayout>
    );
  }

  // Verified users don't belong here — redirect to the appropriate route.
  if (user?.emailVerified) {
    return <Navigate to="/" replace />;
  }

  // No user, no email from signup state, and no token — send to signup.
  if (!user && !emailFromState && !token) {
    return <Navigate to="/signup" replace />;
  }

  return (
    <PublicPageLayout>
      <View style={styles.container}>
        {isTokenError ? (
          <Text style={styles.heading}>
            {tokenVerifyStatus === 'expired'
              ? 'Uh oh! This link has expired.'
              : tokenVerifyStatus === 'invalid'
                ? 'Uh oh! This link is invalid.'
                : 'Uh oh! Verification failed.'}
            {showEmailInput ? ' Request a new verification email below.' : ''}
          </Text>
        ) : (
          <Text style={styles.heading}>
            {'To finish signing up, please click the link in the verification email sent to '}
            <Text style={styles.emailHighlight}>{displayEmail}</Text>
            {'.'}
          </Text>
        )}
        {showEmailInput ? (
          <View style={styles.emailInputRow}>
            <FormInput
              value={inputEmail}
              onChangeText={setInputEmail}
              onSubmit={handleResend}
              placeholderText="Your email address"
            />
            <Button
              variant="primary"
              name={resendCooldown > 0 ? `Retry in ${resendCooldown}s` : 'Send'}
              disabled={!inputEmail || isResendPending || resendCooldown > 0}
              onPress={handleResend}
            />
          </View>
        ) : resendCooldown > 0 ? (
          <Text style={styles.prompt}>{`Email sent! Retry in ${resendCooldown}s`}</Text>
        ) : (
          <Text style={styles.prompt}>
            {isTokenError ? 'Request a new verification email ' : "Didn't receive it? Check your spam or "}
            <HyperLink onPress={handleResend} variant="primary" style={styles.promptLink}>
              {isTokenError ? 'here' : 'resend'}
            </HyperLink>
            {'.'}
          </Text>
        )}
        <View style={styles.iconContainer}>
          <Icon type="email" width={200} height={200} fill={theme.colors.primaryPurple} />
        </View>
        {isTokenError ? (
          <Text style={styles.prompt}>
            {'Already verified? '}
            <HyperLink onPress={() => navigate('/login')} variant="primary" style={styles.promptLink}>
              Log in
            </HyperLink>
            {'.'}
          </Text>
        ) : (
          <Text style={styles.prompt}>
            {'Wrong email? '}
            <HyperLink onPress={() => navigate(-1)} variant="primary" style={styles.promptLink}>
              Go back
            </HyperLink>
            {'.'}
          </Text>
        )}
      </View>
    </PublicPageLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xlarge,
    padding: theme.spacing.xxlarge,
    maxWidth: '75%',
    alignSelf: 'center',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.xxlarge,
  },
  heading: {
    fontSize: theme.fontSizes.xmedium,
    fontWeight: theme.fontWeights.bold,
    color: theme.colors.navy,
    textAlign: 'center',
  },
  emailHighlight: {
    fontWeight: theme.fontWeights.bold,
    color: theme.colors.primaryPurple,
    fontSize: theme.fontSizes.xmedium,
  },
  prompt: {
    fontSize: theme.fontSizes.small,
    color: theme.colors.navy,
    textAlign: 'center',
  },
  promptLink: {
    fontSize: theme.fontSizes.small,
    textDecorationLine: 'underline',
  },
  emailInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.medium,
    width: '100%',
    justifyContent: 'center',
    paddingTop: theme.spacing.xlarge,
  },
});
